import { Injectable } from '@angular/core';
import { API_ENDPOINT, GLOBAL_DATA_KEY, SESSION_STORAGE_KEY, getApiPath, getSaucerLogActionByRoute } from '@core/configs';
import { Router } from '@angular/router';
import { DynamodbKeysRequestModel } from '@core/models/request/check-sheet.model';
import { ApiService, SessionStorageService } from '@core/services/common';
import { GlobalDataService } from '../common/global-data.service';
import { AttentionListRequestModel } from '@core/models/request';
import { tap, map, catchError, switchMap } from 'rxjs/operators';
import { EMPTY, Observable, timer } from 'rxjs';
import { DeviceRegistrationsService } from './device-registrations.service';
import { LOG_CONTENT, LOG_RESULT, LOG_STATUS, LOG_TYPE, NativeDiagnosticLogService } from '../diagnostic-log/native-diagnostic-log.service';
import { SaucerLogService } from '../saucer-logs/saucer-log.service';

@Injectable({
  providedIn: 'root',
})
export class CheckSheetService {
  constructor(
    private readonly http: ApiService,
    private readonly globalDataService: GlobalDataService,
    private readonly sessionStorageService: SessionStorageService,
    private readonly deviceRegistrationsService: DeviceRegistrationsService,
    private readonly nativeDiagnosticLogService: NativeDiagnosticLogService,
    private readonly router: Router,
    private readonly saucerLogService: SaucerLogService
  ) {
  }

  getByOffice() {
    const corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const partitionKey = `corp_${corpPrefix}#office_${officeCd}`;
    const url = `${API_ENDPOINT.checkSheet.getByOffice}`;
    const body = new DynamodbKeysRequestModel(partitionKey);
    const deviceToken = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.deviceToken) || '';
    const logId = this.nativeDiagnosticLogService.generateLogId();
    const attempt = (remaining: number): Observable<any> =>
      this.http.postSilent(url, body).pipe(
        catchError(err => {
          if (remaining > 0) {
            console.log(`[CheckSheet] getByOffice failed, retrying in 1s... (${remaining} left)`);
            this.nativeDiagnosticLogService.sendCareMoniLog({
              logType: LOG_TYPE.OTHER,
              subject: LOG_CONTENT.RETRY,
              status: LOG_STATUS.START,
              message: 11 - remaining,
              logId
            });
            return timer(60000).pipe(switchMap(() => attempt(remaining - 1)));
          }
          console.log("[CheckSheet] getByOffice failed, no more retries left. Returning EMPTY.");
          // alert("ネットワーク接続が失敗しました。アプリを再起動してください。");
          this.writeClientLog(`${deviceToken} | error: ${this.stringifyError(err)}`);
          this.nativeDiagnosticLogService.sendCareMoniLog({
            logType: LOG_TYPE.API,
            subject: getApiPath(url),
            status: err?.status ?? LOG_STATUS.UNDEFINED,
            message: `${LOG_RESULT.ERROR} ${LOG_CONTENT.RETRY_FAIL} | ${this.stringifyError(err)}`,
            logId
          });
          (window as any).AndroidBridge?.onNetworkError();
          return EMPTY;
        })
      );

    return attempt(10).pipe(
      tap((res: any) => {
        this.writeClientLog(deviceToken);
        const ckDataChangedAt = res?.result?.ckDataChangedAt;
        if (!ckDataChangedAt) return;
        const ckDataChangedByDevice = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.ckDataChangedByDevice);
        if (ckDataChangedAt === ckDataChangedByDevice) return;
        if (!deviceToken) return;
        this.deviceRegistrationsService.updateCKDataChangedAt(partitionKey, deviceToken, ckDataChangedAt).subscribe({
          next: () => this.sessionStorageService.setSessionData(SESSION_STORAGE_KEY.ckDataChangedByDevice, ckDataChangedAt),
          error: (e) => console.warn('[CheckSheet] updateDeviceCKData failed:', e)
        });
      }),
      map((res: any) => {
        if (res?.status === 200) {
          return { ...res, result: res.result?.data ?? null };
        }
        return res;
      })
    );
  }

  private writeClientLog(message: string) {
    const url = `${API_ENDPOINT.clientLog.write}`;
    this.http.postSilent(url, { message }).subscribe({
      error: () => { }
    });
  }

  private stringifyError(err: any): string {
    if (err == null) return '';
    if (typeof err === 'string') return err;
    const status = err.status != null ? `status ${err.status}` : '';
    const detail = err.message ?? err.statusText ?? JSON.stringify(err);
    return status ? `${status} - ${detail}` : detail;
  }

  getByUnit() {
    const corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const url = `${API_ENDPOINT.checkSheet.getByUnit}`;
    const partitionKey = `corp_${corpPrefix}#office_${officeCd}`;
    const sortKey = `block_20#sub_04#unit_12`;
    const body = new DynamodbKeysRequestModel(partitionKey, sortKey);
    return this.http.post(url, body);
  }

  getAttentionList(sDate:string, eDate:string, daiList: string[]) {
    let body = new AttentionListRequestModel()
    body.CorpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    body.CorpCd = this.globalDataService.get(GLOBAL_DATA_KEY.corpCd);
    body.Officecd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    body.Sdate = sDate;
    body.Edate = eDate;
    body.DaiList = daiList;

    const url = `${API_ENDPOINT.bath.getAttentionListWithRange}`;
    return this.http.post(url, body);
  }

  manualSyncCkdata(officeType: number) {
    const corpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const url = `${API_ENDPOINT.checkSheet.manualSyncCkdata}`;
    const body = {
      corpPrefix,
      officeCd,
      officeType
    };
    const matchedAction = getSaucerLogActionByRoute(this.router.url);
    return this.http.post(url, body).pipe(
      catchError((err) => {
        this.saucerLogService.error(
          {
            apiPath: getApiPath(url),
            content: '更新失敗',
            statusCode: err?.status,
            errorMessage: err?.message
          },
          {
            action: matchedAction?.UPDATE
          }
        );
        throw err;
      })
    );
  }

  getValidSchedulePeriod(daiCds: string[], anchorDate: string) {
    const url = `${API_ENDPOINT.checkSheet.getValidSchedulePeriod}`;
    const body = {
      daiCds,
      anchorDate
    };
    return this.http.post(url, body);
  }
}
